/**
 * PlaceholderTextarea - Textarea with @ autocomplete for medical document placeholders
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { ALL_PLACEHOLDERS, PLACEHOLDER_CATEGORIES } from '@/services/placeholderEngine';

export default function PlaceholderTextarea({ value, onChange, placeholder, rows = 12, className = '' }) {
  const [showMenu, setShowMenu] = useState(false);
  const [menuFilter, setMenuFilter] = useState('');
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const textareaRef = useRef(null);
  const menuRef = useRef(null);

  const filteredItems = menuFilter
    ? ALL_PLACEHOLDERS.filter(p =>
        p.key.toLowerCase().includes(menuFilter.toLowerCase()) ||
        p.label.toLowerCase().includes(menuFilter.toLowerCase())
      )
    : ALL_PLACEHOLDERS;

  const getCaretCoordinates = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return { top: 0, left: 0 };

    // Create a mirror div to calculate caret position
    const mirror = document.createElement('div');
    const style = window.getComputedStyle(textarea);
    const props = ['fontFamily', 'fontSize', 'fontWeight', 'lineHeight', 'paddingTop', 'paddingLeft', 'paddingRight', 'borderTopWidth', 'borderLeftWidth', 'letterSpacing', 'wordSpacing', 'whiteSpace', 'wordWrap', 'overflowWrap'];
    props.forEach(p => { mirror.style[p] = style[p]; });
    mirror.style.position = 'absolute';
    mirror.style.visibility = 'hidden';
    mirror.style.width = style.width;
    mirror.style.height = 'auto';
    mirror.style.overflow = 'hidden';

    const text = textarea.value.substring(0, textarea.selectionStart);
    mirror.textContent = text;
    const span = document.createElement('span');
    span.textContent = '|';
    mirror.appendChild(span);
    document.body.appendChild(mirror);

    const rect = textarea.getBoundingClientRect();
    const spanRect = span.getBoundingClientRect();
    const mirrorRect = mirror.getBoundingClientRect();

    const top = rect.top + (spanRect.top - mirrorRect.top) - textarea.scrollTop + 24;
    const left = rect.left + (spanRect.left - mirrorRect.left) - textarea.scrollLeft;

    document.body.removeChild(mirror);
    return { top: Math.min(top, rect.bottom), left: Math.min(left, rect.right - 280) };
  }, []);

  const insertPlaceholder = useCallback((key) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const pos = textarea.selectionStart;
    const textBefore = value.substring(0, pos);
    const textAfter = value.substring(pos);

    // Find the @ that triggered the menu
    const atIndex = textBefore.lastIndexOf('@');
    if (atIndex === -1) return;

    const newText = textBefore.substring(0, atIndex) + `@${key}` + textAfter;
    onChange(newText);
    setShowMenu(false);
    setMenuFilter('');

    // Restore focus after React re-render
    requestAnimationFrame(() => {
      textarea.focus();
      const newPos = atIndex + key.length + 1;
      textarea.setSelectionRange(newPos, newPos);
    });
  }, [value, onChange]);

  const handleKeyDown = useCallback((e) => {
    if (!showMenu) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, filteredItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      if (filteredItems[selectedIndex]) {
        insertPlaceholder(filteredItems[selectedIndex].key);
      }
    } else if (e.key === 'Escape') {
      setShowMenu(false);
      setMenuFilter('');
    }
  }, [showMenu, filteredItems, selectedIndex, insertPlaceholder]);

  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    onChange(newValue);

    const pos = e.target.selectionStart;
    const textBefore = newValue.substring(0, pos);
    const atMatch = textBefore.match(/@([A-Z_a-z]*)$/);

    if (atMatch) {
      setMenuFilter(atMatch[1]);
      setShowMenu(true);
      setSelectedIndex(0);
      const coords = getCaretCoordinates();
      setMenuPosition(coords);
    } else {
      setShowMenu(false);
      setMenuFilter('');
    }
  }, [onChange, getCaretCoordinates]);

  // Close menu on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && e.target !== textareaRef.current) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Scroll selected item into view
  useEffect(() => {
    if (showMenu && menuRef.current) {
      const selected = menuRef.current.querySelector('[data-selected="true"]');
      if (selected) selected.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, showMenu]);

  return (
    <div className="relative" data-testid="placeholder-textarea-wrapper">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || 'Digite o conteudo do documento. Use @ para inserir placeholders...'}
        rows={rows}
        className={`w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-mono leading-relaxed focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none resize-y ${className}`}
        data-testid="placeholder-textarea"
        style={{ tabSize: 4 }}
      />

      {/* Autocomplete Menu */}
      {showMenu && filteredItems.length > 0 && (
        <div
          ref={menuRef}
          className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto w-72"
          style={{ top: menuPosition.top, left: menuPosition.left }}
          data-testid="placeholder-menu"
        >
          <div className="sticky top-0 bg-gray-50 px-3 py-2 border-b text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Placeholders - {filteredItems.length} resultado(s)
          </div>
          {filteredItems.map((item, index) => (
            <button
              key={item.key}
              type="button"
              data-selected={index === selectedIndex}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-primary/10 flex items-center justify-between transition-colors ${
                index === selectedIndex ? 'bg-primary/10 text-primary' : 'text-gray-700'
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                insertPlaceholder(item.key);
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div>
                <span className="font-mono font-semibold text-primary">@{item.key}</span>
                <span className="ml-2 text-xs text-gray-400">{item.label}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Helper text */}
      <p className="text-xs text-gray-400 mt-1">
        Digite <span className="font-mono font-bold text-primary">@</span> para inserir dados automaticos do paciente, medico ou clinica
      </p>
    </div>
  );
}
